import React, { memo, useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useParams } from 'react-router-dom'
import { SUBSCRIBER_NOTIFICATION_TYPE } from 'apps/fleet-planner/constants/loads'
import { useShipmentApi } from 'apps/fleet-planner/hooks'
import { ICustomerContact, ILoadSettings } from 'apps/fleet-planner/types'
import { fetchLoadDetails } from 'apps/fleet-planner/utils/actions/loadDetails'
import { FormModal, ToastState } from 'transfix-ui'
import { SubscribersAddSearchBar } from './SubscribersAddSearchBar'
import { SubscribersSettingsModalRow } from './SubscribersSettingsModalRow'
import { SubscriberSettingsModalText } from './SubscribersSettingsModalText'

interface ISubscribersSettingsModalProps {
  isOpen: boolean
  subscribers?: ICustomerContact[]
  loadSettings?: ILoadSettings
  isExternal?: boolean
  customerContact?: ICustomerContact
  onClose: () => void
  onSuccess: (toastState: Omit<ToastState, 'toastOpen'>) => void
}

const SUBSCRIBER_TOAST_MESSAGE = 'Notification settings updated.'

export const SubscribersSettingsModal = memo<ISubscribersSettingsModalProps>(
  ({
    isOpen,
    onClose,
    onSuccess,
    loadSettings,
    subscribers: subscribersProp = [],
    isExternal = false,
    customerContact,
  }) => {
    const { id } = useParams<{ id: string }>()
    const dispatch = useDispatch()
    const [subscribersList, setSubscribersList] = useState<ICustomerContact[]>(subscribersProp)
    const { error: apiError, updateShipmentSubscribers } = useShipmentApi()

    const {
      notifySubscribersAbout: notifySubscribersOptionProp = SUBSCRIBER_NOTIFICATION_TYPE.ACTIVE_STATUSES,
    } = loadSettings || {}
    const [notifySubscribersOption, setNotifySubscribersOption] = useState<
      SUBSCRIBER_NOTIFICATION_TYPE
    >(notifySubscribersOptionProp)

    useEffect(() => {
      if (subscribersProp.length !== 0) {
        setSubscribersList(subscribersProp)
      }
    }, [subscribersProp])

    const cancelModalHandler = () => {
      setSubscribersList(subscribersProp)
      onClose()
    }

    const onChangeSettingsHandler = (newOptionValue: string) => {
      setNotifySubscribersOption(newOptionValue as SUBSCRIBER_NOTIFICATION_TYPE)
    }

    const submitSubscriberRequest = async () => {
      if (subscribersList.length !== 0) {
        const response = await updateShipmentSubscribers(subscribersList, notifySubscribersOption)
        if (response) return response
      }
      return undefined
    }

    const submitHandler = async () => {
      const response = await submitSubscriberRequest()
      if (response) {
        // Fetch load details in order to display updated smsStatusUpdatesEnabled value.
        if (id) dispatch(fetchLoadDetails(id))
        onSuccess({
          toastType: 'default',
          toastMessage: SUBSCRIBER_TOAST_MESSAGE,
        })
        onClose()
      }
    }

    const addSubscriberHandler = (newSubscriber: ICustomerContact) => {
      if (!subscribersList.some(sub => sub.email === newSubscriber.email)) {
        setSubscribersList([newSubscriber, ...subscribersList])
      }
    }

    const removeSubscriberHandler = (subscriber: ICustomerContact) => {
      if (subscribersList.length !== 0) {
        setSubscribersList(subscribersList.filter(item => item !== subscriber))
      }
    }

    const subscribersFullList = () => {
      if (subscribersList.length === 0) {
        return <></>
      }

      return subscribersList?.map(sub => (
        <SubscribersSettingsModalRow
          key={sub.id}
          subscriber={sub}
          onRemove={removeSubscriberHandler}
          isExternal={isExternal}
          shipmentCustomerContact={customerContact}
        />
      ))
    }

    return (
      <FormModal
        error={apiError}
        size={4}
        title='Status Notifications'
        open={isOpen}
        actions={[
          {
            label: 'Cancel',
            type: 'dismiss',
            action: cancelModalHandler,
          },
          {
            label: 'Save',
            type: 'confirm',
            action: submitHandler,
          },
        ]}
        onClose={onClose}>
        <div className='body-medium pb3'>People to notify</div>
        <SubscribersAddSearchBar onAddSubscriber={addSubscriberHandler} />
        <div className='pt2 pb3 border-bottom-disabled'>{subscribersFullList()}</div>
        <SubscriberSettingsModalText
          notifySubscribersOption={notifySubscribersOption}
          onChange={onChangeSettingsHandler}
        />
      </FormModal>
    )
  }
)
